import axios from 'axios';
import Alert from '@mui/lab/Alert';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import { useHistory } from 'react-router';
import Button from '@mui/material/Button';
import Loader from 'react-loader-spinner';
import MDEditor from '@uiw/react-md-editor';
import { useDropzone } from 'react-dropzone';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useState, useEffect } from 'react';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import DesktopDatePicker from '@mui/lab/DesktopDatePicker';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterDateFns from '@mui/lab/AdapterDateFns';

import { formatDate } from '../../../utils/moment.util';
import DashboardNavBar from '../../../components/Dashboard/Common/Navbar';
import DashboardSideMenu from '../../../components/Dashboard/Common/SidebarMenu';
import {
  classes,
  thumb,
  thumbInner,
  thumbsContainer,
  img,
  fileIconColor,
} from '../styles';

const baseUrl = process.env.REACT_APP_BASE_URL;
const token = localStorage.getItem('lhcfToken');

const DashboardPublishEventPage = () => {
  const history = useHistory();

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbarState, setSnackbarState] = useState({
    warningOpen: false,
    errorOpen: false,
    successOpen: false,
    vertical: 'center',
    horizontal: 'center',
  });
  const [event, setEvent] = useState({
    title: '',
    location: '',
    time: '',
    excerpt: '',
    body: '',
    image: '',
  });
  const [date, setDate] = useState(new Date());

  const { warningOpen, successOpen, errorOpen, vertical, horizontal } =
    snackbarState;

  const handleDateChange = (newValue) => {
    setDate(newValue);
  };

  const handleMarkDownBodyChange = (newValue) => {
    setEvent((prevState) => {
      return { ...prevState, body: newValue };
    });
  };

  const handleClose = () => {
    setSnackbarState({
      ...snackbarState,
      warningOpen: false,
      errorOpen: false,
      successOpen: false,
    });
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    onDrop: (acceptedFiles) => {
      setFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
      );
    },
  });

  const thumbs = files.map((file) => (
    <div style={thumb} key={file.name}>
      <div style={thumbInner}>
        <img src={file.preview} style={img} alt="new item" />
      </div>
    </div>
  ));

  const breadcrumbs = [
    <Link
      underline="hover"
      key="1"
      color="inherit"
      href="/dashboard"
      onClick={() => history.push('/dashboard')}
    >
      Dashboard
    </Link>,
    <Link
      underline="hover"
      key="2"
      color="inherit"
      href="/dashboard/events/"
      onClick={() => history.push('/dashboard/gallery-photos')}
    >
      Events
    </Link>,
    <Typography key="3" color="text.primary">
      Publish Event
    </Typography>,
  ];

  const handleInputChange = (e) => {
    const key = e.target.name;
    const value = e.target.value;

    setEvent({ ...event, [key]: value });
  };

  const handleSubmission = (e) => {
    e.preventDefault();

    if (!event.body) {
      return setSnackbarState({
        ...snackbarState,
        warningOpen: true,
      });
    }

    setLoading(true);

    const formData = new FormData();

    formData.append('title', event.title);
    formData.append('location', event.location);
    formData.append('date', formatDate(date, 'YYYY-MM-DD'));
    formData.append('time', event.time);
    formData.append('excerpt', event.excerpt);
    formData.append('body', event.body);

    if (files.length) {
      formData.append('image', files[0]);
    }

    axios
      .post(`${baseUrl}/event`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      })
      .then(
        ({
          data: {
            data: { _id },
          },
        }) => {
          setSnackbarState({ ...snackbarState, successOpen: true });
          setLoading(false);
          setEvent({
            title: '',
            location: '',
            time: '',
            excerpt: '',
            body: '',
            image: '',
          });
          setFiles([]);
          setTimeout(() => {
            history.push(`/dashboard/events/${_id}`);
          }, 5000);
        }
      )
      .catch((err) => {
        setSnackbarState({ ...snackbarState, errorOpen: true });
        setLoading(false);
      });
  };

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks
    files.forEach((file) => URL.revokeObjectURL(file.preview));
  }, [files]);

  return (
    <Box sx={classes.container}>
      <DashboardNavBar />
      <DashboardSideMenu />
      {/* PAGE */}
      <Box sx={classes.pageContainer}>
        <Snackbar
          open={warningOpen}
          autoHideDuration={10000}
          onClose={handleClose}
          anchorOrigin={{ vertical, horizontal }}
        >
          <Alert
            onClose={handleClose}
            severity="warning"
            sx={{ width: '100%' }}
          >
            Details/body of the event is required. Describe the event.
          </Alert>
        </Snackbar>
        <Snackbar
          open={errorOpen}
          autoHideDuration={10000}
          onClose={handleClose}
          anchorOrigin={{ vertical, horizontal }}
        >
          <Alert onClose={handleClose} severity="error" sx={{ width: '100%' }}>
            Could not publish the event. Try again.
          </Alert>
        </Snackbar>
        <Snackbar
          open={successOpen}
          autoHideDuration={10000}
          onClose={handleClose}
          anchorOrigin={{ vertical, horizontal }}
        >
          <Alert
            onClose={handleClose}
            severity="success"
            sx={{ width: '100%' }}
          >
            Event published successfully!
          </Alert>
        </Snackbar>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          {breadcrumbs}
        </Breadcrumbs>
        <Box
          sx={classes.paperContainer}
          component="form"
          onSubmit={handleSubmission}
        >
          <Paper elevation={4} variant="outlined" square>
            <TextField
              id="filled-basic"
              label="Title"
              name="title"
              variant="filled"
              value={event.title}
              onChange={handleInputChange}
              required
              sx={classes.commonTextFieldWidth}
            />

            <br />
            <br />

            <TextField
              id="filled-basic"
              label="Location"
              name="location"
              variant="filled"
              value={event.location}
              onChange={handleInputChange}
              required
              sx={classes.commonTextFieldWidth}
            />

            <br />
            <br />

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DesktopDatePicker
                label="Date"
                value={date}
                inputFormat="dd/MM/yyyy"
                onChange={handleDateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="filled"
                    sx={classes.commonTextFieldWidth}
                  />
                )}
              />
            </LocalizationProvider>

            <br />
            <br />

            <TextField
              id="filled-basic"
              label="Time"
              name="time"
              variant="filled"
              value={event.time}
              onChange={handleInputChange}
              required
              sx={classes.commonTextFieldWidth}
              placeholder="Ex: 10:00 - 11:00"
            />

            <br />
            <br />

            <TextField
              id="standard-multiline-static"
              label="Excerpt"
              name="excerpt"
              multiline
              rows={5}
              variant="filled"
              value={event.excerpt}
              onChange={handleInputChange}
              required
              sx={classes.commonTextFieldWidth}
            />

            <br />
            <br />

            <Typography
              variant="subtitle1"
              component="div"
              sx={{ marginBottom: 3 }}
            >
              Write the details/body of the event in the below editor. The left
              section is the editor and the right section is the preview (how
              the final result will look on the website event page). To write in
              the editor, you will need to use a simple language called,
              Markdown. You can find more about it here:{' '}
              <a
                href="https://www.markdownguide.org/basic-syntax"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://www.markdownguide.org/basic-syntax.{' '}
              </a>{' '}
              You can also use the below left section icons to generate Markdown
              symbols and add formatting to your text.
            </Typography>

            <MDEditor
              height={500}
              value={event.body}
              onChange={handleMarkDownBodyChange}
            />

            <br />
            <br />

            <section style={{ width: '400px' }}>
              <div {...getRootProps({ className: 'dropzone' })}>
                <input {...getInputProps()} />
                <AttachFileIcon style={fileIconColor} />
                <p>Drag and drop image file here, or click to select file *</p>
              </div>
              <aside style={thumbsContainer}>{thumbs}</aside>
            </section>

            <br />

            <Box sx={classes.buttonsContainer}>
              {loading ? (
                <Button variant="contained" color="primary" disabled>
                  <Loader type="Oval" color="#1976d2" height={30} width={50} />
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={classes.buttonMargin}
                    type="submit"
                  >
                    Publish
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => history.push('/dashboard/events')}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardPublishEventPage;
